import { CrossRegionPersistence } from "@brightagency/headstart-common";
import { findIndex } from "lodash";
import { useCallback, useState } from "react";
import createPersistedState from "use-persisted-state";
import { siteId } from "../../constants";
import { screenOrder, Screens, AssessmentState } from "./types";
import { useInterrupts } from "./utils";

const ASSESSMENT_LOCAL_STORAGE_KEY = `headstart.${siteId}.assessment-v2.state`;

const useAssessmentStateGeneric = function<T extends AssessmentState>(
    genericInitialState: T,
    persistedState: typeof useState,
    screenOrder: Screens[],
) {
    const [state, _setState] = persistedState<T>(genericInitialState);
    const { currentScreen } = state;
    const [{ goNextInterrupt, goPrevInterrupt }, { clearInterupts }] = useInterrupts();

    const scrollToTop = useCallback((delay = 0) => {
        window.setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }, delay);
    }, []);

    const setState = useCallback(
        (nextState: Partial<T>) => {
            _setState(old => {
                if (nextState.currentScreen && old.currentScreen !== nextState.currentScreen) {
                    scrollToTop();
                }
                return {
                    ...old,
                    ...nextState,
                };
            });
        },
        [_setState, scrollToTop],
    );

    const reset = useCallback(() => {
        scrollToTop();
        setState(genericInitialState);
        CrossRegionPersistence.clearChosenSuburb();
    }, [scrollToTop, setState, genericInitialState]);

    const getNextScreenIndex = () => {
        return screenOrder[findIndex(screenOrder, s => s === state.currentScreen) + 1];
    };

    const skip = useCallback(() => {
        clearInterupts();
        scrollToTop(currentScreen === 0 ? 500 : 0);
        _setState(old => ({
            ...old,
            currentScreen: screenOrder[findIndex(screenOrder, s => s === old.currentScreen) + 1],
        }));
    }, [_setState, clearInterupts, currentScreen, screenOrder, scrollToTop]);

    const goNext = useCallback(() => {
        if (goNextInterrupt) {
            goNextInterrupt();
        } else {
            skip();
        }
    }, [goNextInterrupt, skip]);

    const goBack = useCallback(() => {
        if (goPrevInterrupt) {
            goPrevInterrupt();
        } else {
            clearInterupts();
            scrollToTop();
            _setState(old => ({
                ...old,
                currentScreen:
                    screenOrder[findIndex(screenOrder, s => s === old.currentScreen) - 1],
            }));
        }
    }, [_setState, clearInterupts, goPrevInterrupt, screenOrder, scrollToTop]);

    const currentScreenIndex = findIndex(screenOrder, s => s === state.currentScreen);

    const isForSelf = state.target === "MYSELF";

    const goToScreen = (screen: Screens) => {
        _setState(old => ({
            ...old,
            currentScreen: screen,
        }));
        scrollToTop();
    };

    const goToScreenIndex = (index: number) => {
        const screen = screenOrder[index];
        goToScreen(screen);
    };

    return {
        state,
        setState,
        goNext,
        reset,
        isForSelf,
        goBack,
        currentScreenIndex,
        getNextScreenIndex,
        scrollToTop,
        goToScreen,
        goToScreenIndex,
        skip,
    };
};

export const useAssessmentState = () =>
    useAssessmentStateGeneric<AssessmentState>(
        {
            currentScreen: screenOrder[0],
            target: undefined,
            showselection:1,
            suburb: '',
            newsuburbname:'',
            age: "",
            lgbtq: undefined,
            atsi: undefined,
            gender_identity: undefined,
            multicultural: undefined,
            specific_to_gender: undefined,
            conditions: [],
            situations: [],
            communities: [],
            active_community: undefined,
        },
        createPersistedState(ASSESSMENT_LOCAL_STORAGE_KEY),
        screenOrder,
    );
